import { partition } from 'lodash'
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { type Writable } from 'type-fest'
import { v4 as uuidv4 } from 'uuid'
import { reservedCustomFieldNames } from '@opallabs/api'
import { type TextInputValidationResult } from '@opallabs/lapidary'
import { type SelectOption } from '../CustomFieldsDrawer'
import {
  type SimplifiedLabel,
  type SimplifiedLabelSet,
} from '../useGetLabelSets'
import {
  type PersistedSimplifiedOption,
  type PairingFormProps,
  type SimplifiedOption,
} from './types'

type InternalStateUpdates = Readonly<{
  addBlankEntry: () => void
  setCustomFieldName: Dispatch<
    SetStateAction<PairingFormProps['customField']['name']>
  >
  setLabelSetId: (newId: PairingFormProps['selectedLabelSetId']) => void
  updateOption: (field: SimplifiedOption) => void
}>

type NonNullableOptionName = NonNullable<SimplifiedOption['optionName']>
type DerivedState = Readonly<{
  /** State of the current value of the custom field name input to keep it in sync with user edits */
  customFieldName: PairingFormProps['customField']['name']
  /** Label IDs assigned to more than one entry */
  duplicateLabelIds: Set<SimplifiedLabel['id']>
  /** Normalized option names that appear more than once — used for O(1) duplicate validation */
  duplicateOptionNames: Set<NonNullableOptionName>
  entries: SimplifiedOption[]
  /** Entries that have identical label assignments */
  entriesWithDuplicateLabelIds: Set<SimplifiedOption['pseudoId']>
  /** Whether the entire form’s data can be submitted */
  isValid: boolean
  /** Labels in the currently-selected label set */
  labels: SimplifiedLabelSet['labels']
  labelSet: SimplifiedLabelSet | undefined
  /** Entries that have identical option names — used in determining whether to show validation failures */
  entriesWithDuplicateOptionNames: Set<SimplifiedOption['pseudoId']>
}>

export type ContextValue = InternalStateUpdates &
  Omit<PairingFormProps, 'onDataChange' | 'onValidityChange'> &
  DerivedState

const PairingFormContext = createContext<ContextValue | undefined>(undefined)
PairingFormContext.displayName =
  'AdminConfiguration: PairingFormContext ← CustomFields'

type PairingFormProviderProps = PairingFormProps &
  Readonly<{
    children: ReactNode
  }>

const isAllWhitespace = (value: string) =>
  value === '' ? false : value.trim() === ''

export const validateName = (
  value: string,
  {
    existingFieldNames,
    originalName,
  }: {
    existingFieldNames?: string[]
    originalName?: string
  } = {}
): TextInputValidationResult => {
  const trimmed = value.trim()

  if (trimmed === '') {
    return { errorMessage: 'Must not be blank', valid: false }
  }

  if (existingFieldNames) {
    const isDuplicate = existingFieldNames.some(
      (name) =>
        name.toLocaleLowerCase() === trimmed.toLocaleLowerCase() &&
        name.toLocaleLowerCase() !== originalName?.toLocaleLowerCase()
    )
    if (isDuplicate) {
      return {
        errorMessage: `There is already an active field named “${trimmed}”`,
        valid: false,
      }
    }
  }

  // Checking this after `existingFieldNames` provides better UX
  if (reservedCustomFieldNames.includes(trimmed.toLocaleLowerCase())) {
    const properNoun =
      trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLocaleLowerCase()
    return {
      errorMessage: `“${properNoun}” is a reserved custom field name`,
      valid: false,
    }
  }

  return { valid: true }
}

/**
 * An option name is valid if
 * * it's not blank (zero-length or just whitespace), or
 * * it's empty (zero-length) and not paired with a label
 */
export const validateOptionName = (
  value: string,
  {
    isDuplicate,
    labelId,
  }: {
    isDuplicate: boolean
    labelId: SimplifiedLabel['id'] | undefined
  }
): TextInputValidationResult => {
  const hasPairedLabel = !!labelId

  if (value === '') {
    if (!hasPairedLabel) return { valid: true }

    return {
      errorMessage: 'Each label must have a paired option',
      valid: false,
    }
  }

  if (isDuplicate) {
    return {
      errorMessage: 'Names must be unique',
      valid: false,
    }
  }

  if (!isAllWhitespace(value)) return { valid: true }

  return validateName(value)
}

/** @private Only exported for tests */
export type ValidationResult = {
  customFieldName: boolean
  labelSetId: boolean
  options: (Pick<SimplifiedOption, 'pseudoId'> &
    Writable<TextInputValidationResult>)[]
}
/** @private Only exported for tests */
export type ValidateParams = Readonly<{
  customFieldName: string
  duplicateLabelIds: Set<SimplifiedLabel['id']>
  entries: SimplifiedOption[]
  existingFieldNames?: string[]
  labelSets: SimplifiedLabelSet[]
  originalName?: string
  selectedLabelSetId: SimplifiedLabelSet['id'] | undefined
}>
/**
 * The form is valid if
 * * each entry with a non-blank label has a non-blank option input
 * * all option inputs are either blank or unique strings
 *
 * @private Only exported for tests
 */
export const validate = ({
  customFieldName,
  duplicateLabelIds,
  entries,
  existingFieldNames,
  labelSets,
  originalName,
  selectedLabelSetId,
}: ValidateParams): [boolean, ValidationResult] => {
  const duplicateNames = getDuplicateOptionNames(entries)

  const validatedOptions: ValidationResult['options'] = []
  entries.forEach(({ labelId, optionName, pseudoId }) => {
    const normalized = (optionName ?? '').trim()
    const isDuplicate = duplicateNames.has(normalized)

    const validationResult = validateOptionName(optionName ?? '', {
      isDuplicate,
      labelId,
    })

    const optionValidation: ValidationResult['options'][number] = {
      pseudoId,
      valid: validationResult.valid,
    }

    if (!validationResult.valid && validationResult.errorMessage) {
      optionValidation.errorMessage = validationResult.errorMessage
    }

    validatedOptions.push(optionValidation)
  })

  const validateLabelSet = (selectedLabelSetId?: string): boolean => {
    if (!selectedLabelSetId) return false

    // Unlikely that `selectedLabelSetId` wouldn't be in the actual list of label sets--probably just a fallback in case
    // the pairing is for a label set that has since been archived or deleted. Better to handle it than just assume it
    // won't happen though.
    const labelSet = labelSets.find(({ id }) => id === selectedLabelSetId)

    return labelSet != null && !labelSet.pairedCustomFieldId
  }

  const result: ValidationResult = {
    customFieldName: validateName(customFieldName, {
      existingFieldNames,
      originalName,
    }).valid,
    labelSetId: validateLabelSet(selectedLabelSetId),
    options: validatedOptions,
  }

  const isValid =
    result.customFieldName &&
    result.labelSetId &&
    duplicateLabelIds.size === 0 &&
    validatedOptions.every(({ valid }) => valid)

  return [isValid, result]
}

/**
 * Ensure the `options` array includes `modifiedEntry`: add it if it’s new, or make sure it has the correct
 * `pseudoPosition` if not.
 *
 * @private Only exported for tests
 */
export const updateOptionsArray = (
  currentOptionsState: SimplifiedOption[],
  modifiedEntry: SimplifiedOption
): SimplifiedOption[] => {
  const existingIndex = currentOptionsState.findIndex(
    ({ pseudoId }) => pseudoId === modifiedEntry.pseudoId
  )

  if (existingIndex < 0) return [...currentOptionsState, modifiedEntry]

  return currentOptionsState.map((option, index) => {
    if (index !== existingIndex) return option

    return {
      ...modifiedEntry,
      pseudoPosition: modifiedEntry.pseudoPosition ?? option.pseudoPosition,
    }
  })
}

/** Returns normalized option names that appear more than once */
const getDuplicateOptionNames = (
  entries: SimplifiedOption[]
): DerivedState['duplicateOptionNames'] => {
  const nameCounts = new Map<string, number>()
  entries.forEach(({ optionName }) => {
    const normalized = (optionName ?? '').trim()
    if (normalized) {
      nameCounts.set(normalized, (nameCounts.get(normalized) ?? 0) + 1)
    }
  })

  return new Set<NonNullableOptionName>(
    [...nameCounts.entries()]
      .filter(([, count]) => count > 1)
      .map(([name]) => name)
  )
}

/** @private Only exported for tests */
export const detectDuplicateOptionNames = (entries: SimplifiedOption[]) => {
  const pseudoIdsByOptionName = new Map<
    NonNullableOptionName,
    Set<SimplifiedOption['pseudoId']>
  >()

  entries.forEach((entry) => {
    if (entry.optionName && entry.optionName.trim() !== '') {
      const normalized = entry.optionName.trim()
      if (!pseudoIdsByOptionName.has(normalized))
        pseudoIdsByOptionName.set(normalized, new Set())
      pseudoIdsByOptionName.get(normalized)!.add(entry.pseudoId)
    }
  })

  return [...pseudoIdsByOptionName.values()].reduce((memo, ids) => {
    if (ids.size > 1) ids.forEach((id) => memo.add(id))
    return memo
  }, new Set<SimplifiedOption['pseudoId']>())
}

/** Returns label IDs that appear on more than one entry */
const getDuplicateLabelIds = (
  entries: SimplifiedOption[]
): DerivedState['duplicateLabelIds'] => {
  const idCounts = new Map<string, number>()
  entries.forEach(({ labelId }) => {
    if (labelId && labelId.trim() !== '') {
      idCounts.set(labelId, (idCounts.get(labelId) ?? 0) + 1)
    }
  })

  return new Set<SimplifiedLabel['id']>(
    [...idCounts.entries()].filter(([, count]) => count > 1).map(([id]) => id)
  )
}

/** @private Only exported for tests */
export const detectDuplicateLabelIds = (entries: SimplifiedOption[]) => {
  const pseudoIdsByLabelId = new Map<
    SimplifiedLabel['id'],
    Set<SimplifiedOption['pseudoId']>
  >()

  entries.forEach((entry) => {
    if (entry.labelId && entry.labelId.trim() !== '') {
      if (!pseudoIdsByLabelId.has(entry.labelId))
        pseudoIdsByLabelId.set(entry.labelId, new Set())
      pseudoIdsByLabelId.get(entry.labelId)!.add(entry.pseudoId)
    }
  })

  return [...pseudoIdsByLabelId.values()].reduce((memo, ids) => {
    if (ids.size > 1) ids.forEach((id) => memo.add(id))
    return memo
  }, new Set<SimplifiedOption['pseudoId']>())
}

/** Narrow `SimplifiedOption` to `PersistedSimplifiedOption` or `UnpersistedSimplifiedOption` */
function isPersistedOption(
  option: SimplifiedOption
): option is PersistedSimplifiedOption {
  return option.optionId !== undefined && option.position !== undefined
}

/**
 * ‘Base’ value for the `pseudoPosition` on manually-added entries, making it easy to keep them sorted at the end of the
 * entries array.
 *
 * @private Only exported for tests
 */
export const ManuallyAddedEntryPseudoPositionOffset = 1_000_000

/** @private Only exported for tests */
export const temporaryIdPrefix = 'temporary-'
/**
 * Builds the entries array from persisted and unpersisted options. Persisted options appear first
 * (sorted by position), then unpersisted options. Users add entries manually and assign labels via
 * dropdowns.
 *
 * @private Only exported for tests
 */
export const buildEntries = ({
  options,
  isReadonly = false,
}: {
  options: SimplifiedOption[]
  /**
   * Readonly mode skips blank entries — ones where no label is selected and the option’s name and id are empty.
   */
  isReadonly?: boolean
}): SimplifiedOption[] => {
  const nonEmptyOptions = options.filter((option) => {
    if ((option.labelId ?? '').trim() !== '') return true

    if ((option.optionName ?? '').trim() !== '') return true

    if (option.optionId && !option.optionId.startsWith(temporaryIdPrefix))
      return true

    if (option.pseudoPosition !== undefined) return !isReadonly

    return false
  })

  const [persisted, unpersisted] = partition(nonEmptyOptions, isPersistedOption)

  const sortedPersisted = persisted.sort((a, b) => a.position - b.position)
  const entries: SimplifiedOption[] = [...sortedPersisted, ...unpersisted]

  const needsInitialSetup = entries.every(
    (entry) => entry.pseudoPosition === undefined
  )

  if (needsInitialSetup) {
    let nextPosition = 0
    const finalEntries = entries.map((entry) => ({
      ...entry,
      pseudoPosition: nextPosition++,
    }))

    return finalEntries
  } else {
    const entriesWithPositions: SimplifiedOption[] = []
    const entriesWithoutPositions: SimplifiedOption[] = []

    entries.forEach((entry) => {
      if (entry.pseudoPosition !== undefined) {
        entriesWithPositions.push(entry)
      } else {
        entriesWithoutPositions.push(entry)
      }
    })

    let nextPosition = 0
    entriesWithPositions.forEach((entry) => {
      if (entry.pseudoPosition! < ManuallyAddedEntryPseudoPositionOffset) {
        nextPosition = Math.max(nextPosition, entry.pseudoPosition! + 1)
      }
    })

    entriesWithoutPositions.forEach((entry) => {
      entriesWithPositions.push({
        ...entry,
        pseudoPosition: nextPosition++,
      })
    })

    return entriesWithPositions.sort((a, b) => {
      const aIsManuallyAdded =
        a.pseudoPosition! >= ManuallyAddedEntryPseudoPositionOffset
      const bIsManuallyAdded =
        b.pseudoPosition! >= ManuallyAddedEntryPseudoPositionOffset

      if (aIsManuallyAdded !== bIsManuallyAdded)
        return aIsManuallyAdded ? 1 : -1

      return a.pseudoPosition! - b.pseudoPosition!
    })
  }
}

// Trivial equality comparison
const shallowArrayEqual = <T,>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false
  return a.every((item, i) => item === b[i])
}

type PersistableSimplifiedOption = SimplifiedOption & {
  optionName: NonNullableOptionName
}
function isPersistableOption(
  option: SimplifiedOption
): option is PersistableSimplifiedOption {
  return (option.optionName ?? '').trim() !== ''
}

export const prepareEntriesForPersistence = (
  options: SimplifiedOption[]
): SelectOption[] => {
  const persistable = options.filter(isPersistableOption)

  return persistable.map((option, index) => {
    const id =
      !option.optionId || option.optionId.startsWith(temporaryIdPrefix)
        ? uuidv4()
        : option.optionId

    return {
      id,
      display_name: option.optionName,
      // All valid options are being created or updated, so existing persisted positions are irrelevant
      position: index,
      relationships: {
        paired_label: {
          data: option.labelId ? { id: option.labelId, type: 'label' } : null,
        },
      },
    } satisfies SelectOption
  })
}

export const PairingFormProvider = ({
  canChangeCustomFieldName,
  canChangeLabelSet = false,
  children,
  customField,
  existingFieldNames,
  isReadonly,
  labelSets,
  onDataChange,
  onValidityChange,
  options: initialOptions,
  selectedLabelSetId: initialSelectedLabelSetId,
}: PairingFormProviderProps) => {
  const [customFieldName, setCustomFieldName] = useState(customField.name)
  const [options, setOptions] = useState(initialOptions)
  const [labelSetId, setLabelSetId] = useState<
    PairingFormProviderProps['selectedLabelSetId']
  >(initialSelectedLabelSetId)

  const [entriesWithDuplicateOptionNames, setEntriesWithDuplicateOptionNames] =
    useState<DerivedState['entriesWithDuplicateOptionNames']>(new Set())
  const [entriesWithDuplicateLabelIds, setEntriesWithDuplicateLabelIds] =
    useState<DerivedState['entriesWithDuplicateLabelIds']>(new Set())

  // These `useEffect`s force state updates when props change. This is necessary for `<LabelSetPairingModal>`'s
  // 'Review' step.
  useEffect(() => {
    setOptions((current) =>
      shallowArrayEqual(current, initialOptions) ? current : initialOptions
    )
  }, [initialOptions])
  useEffect(() => setCustomFieldName(customField.name), [customField.name])
  useEffect(
    () => setLabelSetId(initialSelectedLabelSetId),
    [initialSelectedLabelSetId]
  )

  const [labelSet, labels]: [
    SimplifiedLabelSet | undefined,
    SimplifiedLabelSet['labels'],
  ] = useMemo(() => {
    const labelSet = labelSets.find(({ id }) => id === labelSetId)
    const labels = labelSet?.labels ?? []

    return [labelSet, labels]
  }, [labelSetId, labelSets])

  // Prefill the custom field name with the selected label set's name when the user hasn't typed
  // anything yet. The save path in Loupe renames the label set to match the field, so starting
  // them in sync avoids surprising the user. Functional `setCustomFieldName` keeps this out of the
  // effect deps and lets a user-typed value survive label-set changes.
  useEffect(() => {
    if (!labelSet?.name) return
    setCustomFieldName((current) =>
      current && current.trim() !== '' ? current : labelSet.name
    )
  }, [labelSet?.name])

  // Wraps `setLabelSetId` to also reset options when the label set changes — discards temporary
  // entries from the previous set and clears stale label pairings in the same update.
  const changeLabelSetId = useCallback(
    (newId: PairingFormProps['selectedLabelSetId']) => {
      setLabelSetId(newId)

      const newLabelSet = labelSets.find(({ id }) => id === newId)
      const newLabelIds = new Set(
        (newLabelSet?.labels ?? []).map(({ id }) => id)
      )

      setOptions((currentOptions) => {
        const persisted = currentOptions.filter(
          (option) =>
            option.optionId !== undefined &&
            !option.optionId.startsWith(temporaryIdPrefix)
        )
        const withClearedLabels = persisted.map((option) =>
          option.labelId && !newLabelIds.has(option.labelId)
            ? { ...option, labelId: undefined }
            : option
        )

        // auto-pair unpaired options if an exact match exists
        const newLabels = newLabelSet?.labels ?? []
        const claimedLabelIds = new Set(
          withClearedLabels
            .map((opt) => opt.labelId)
            .filter((id): id is string => !!id)
        )
        const labelByName = new Map(
          newLabels
            .filter(({ id }) => !claimedLabelIds.has(id))
            .map((label) => [label.name, label])
        )
        const withAutoMatched = withClearedLabels.map((option) => {
          if (option.labelId) return option
          const optionName = (option.optionName ?? '').trim()
          if (!optionName) return option
          const matchedLabel = labelByName.get(optionName)
          if (!matchedLabel) return option
          labelByName.delete(optionName)
          return { ...option, labelId: matchedLabel.id }
        })

        const hasChanges =
          persisted.length !== currentOptions.length ||
          withAutoMatched.some((opt, i) => opt !== persisted[i])

        return hasChanges ? withAutoMatched : currentOptions
      })
    },
    [labelSets]
  )

  const entries = useMemo(
    () => buildEntries({ options, isReadonly }),
    [options, isReadonly]
  )

  // Make sure all `entries` members are present in `options` to preserve `pseudoId`s and `pseudoPosition`s, ensuring
  // stable ordering
  useEffect(() => {
    setOptions((currentOptions) => {
      const entriesByPseudoId = new Map(entries.map((e) => [e.pseudoId, e]))
      let hasChanges = false

      const updatedOptions = currentOptions.map((option) => {
        const entry = entriesByPseudoId.get(option.pseudoId)
        if (entry && option.pseudoPosition !== entry.pseudoPosition) {
          hasChanges = true
          return { ...option, pseudoPosition: entry.pseudoPosition }
        }
        return option
      })

      const existingPseudoIds = new Set(
        currentOptions.map((opt) => opt.pseudoId)
      )
      const missingEntries = entries.filter(
        (entry) => !existingPseudoIds.has(entry.pseudoId)
      )

      if (missingEntries.length > 0) hasChanges = true

      return hasChanges
        ? [...updatedOptions, ...missingEntries]
        : currentOptions
    })
  }, [entries])

  // `useEffect` ensures `optionName` validations run on initial render when `<PairingForm>` is in readonly mode
  useEffect(() => {
    const duplicateIds = detectDuplicateOptionNames(entries)
    setEntriesWithDuplicateOptionNames(duplicateIds)
  }, [entries])

  const duplicateOptionNames = useMemo(
    () => getDuplicateOptionNames(entries),
    [entries]
  )

  const duplicateLabelIds = useMemo(
    () => getDuplicateLabelIds(entries),
    [entries]
  )

  useEffect(() => {
    setEntriesWithDuplicateLabelIds(detectDuplicateLabelIds(entries))
  }, [entries])

  const [isValid] = useMemo(() => {
    const guaranteedCustomFieldName = customFieldName ?? ''

    return validate({
      customFieldName: guaranteedCustomFieldName,
      duplicateLabelIds,
      entries,
      existingFieldNames,
      labelSets,
      originalName: customField.name,
      selectedLabelSetId: labelSetId,
    })
  }, [
    customFieldName,
    customField.name,
    duplicateLabelIds,
    entries,
    existingFieldNames,
    labelSetId,
    labelSets,
  ])

  useEffect(() => onValidityChange?.(isValid), [isValid, onValidityChange])

  useEffect(() => {
    onDataChange?.({
      customFieldName: customFieldName ?? '',
      labelSetId: labelSetId ?? '',
      options,
    })
  }, [customFieldName, labelSetId, onDataChange, options])

  const updateOption = useCallback((modifiedEntry: SimplifiedOption) => {
    setOptions((currentOptionsState) =>
      updateOptionsArray(currentOptionsState, modifiedEntry)
    )
  }, [])

  const addBlankEntry = useCallback(() => {
    const blankOption: SimplifiedOption = {
      labelId: undefined,
      optionId: undefined,
      optionName: '',
      position: undefined,
      pseudoId: `blank-${uuidv4()}`,
      pseudoPosition:
        entries.length > 0
          ? Math.max(...entries.map((entry) => entry.pseudoPosition ?? 0)) +
            ManuallyAddedEntryPseudoPositionOffset
          : ManuallyAddedEntryPseudoPositionOffset,
    }

    setOptions((currentOptionsState) => [...currentOptionsState, blankOption])
  }, [entries])

  const value: ContextValue = useMemo(
    () =>
      ({
        addBlankEntry,
        canChangeCustomFieldName,
        canChangeLabelSet,
        customField,
        customFieldName,
        existingFieldNames,
        duplicateLabelIds,
        duplicateOptionNames,
        entries,
        entriesWithDuplicateLabelIds,
        entriesWithDuplicateOptionNames,
        isReadonly,
        isValid,
        labels,
        labelSet,
        labelSets,
        options,
        setCustomFieldName,
        setLabelSetId: changeLabelSetId,
        updateOption,
      }) as const,
    [
      addBlankEntry,
      canChangeCustomFieldName,
      canChangeLabelSet,
      changeLabelSetId,
      customField,
      customFieldName,
      existingFieldNames,
      duplicateLabelIds,
      duplicateOptionNames,
      entries,
      entriesWithDuplicateLabelIds,
      entriesWithDuplicateOptionNames,
      isReadonly,
      isValid,
      labels,
      labelSet,
      labelSets,
      options,
      updateOption,
    ]
  )

  return (
    <PairingFormContext.Provider value={value}>
      {children}
    </PairingFormContext.Provider>
  )
}

export const usePairingFormState = (): ContextValue => {
  const context = useContext(PairingFormContext)
  if (!context) {
    throw new Error(
      'usePairingFormState must be used within a PairingFormProvider'
    )
  }
  return context
}
